import { TestBlock } from '@/types/report-payload';
import { getFootnoteAdditionalHeight } from '../footnotes';
import { Section } from '../types';
import { ReportData } from '../types';
import {
    BLOCK_GAP,
    CONTENT_WIDTH,
    DEFAULT_BLOCK_HEIGHT,
    DEFAULT_TITLE_HEIGHT,
    EMPTY_PLACEHOLDER_HEIGHT,
    SECTION_GAP,
} from './constants';
import { PageManager } from './PageManager';
import { splitHtmlContent } from './split-utils';
import { RenderableSection } from './types';
import { createSplitBlockId, getBlockMeasurementId, stripSplitBlockId } from './utils';
import type { ReportFootnotePlan } from '../footnotes';

interface ProcessTestsOptions {
    section: Section;
    data: ReportData;
    measurements: Record<string, number>;
    pageManager: PageManager;
    editingBlockId?: string | number | null;
    footnotePlan?: ReportFootnotePlan;
}

interface BlockQueueItem {
    block: TestBlock;
    measuredHeight: number | undefined;
    pinWhole?: boolean;
}

export function processTestsSection({
    section,
    data,
    measurements,
    pageManager,
    editingBlockId,
    footnotePlan,
}: ProcessTestsOptions) {
    const titleHeight = measurements[`section-${section.id}-title`] || DEFAULT_TITLE_HEIGHT;
    const allBlocks = data.tests?.blocks || [];
    let isFirstChunk = true;

    if (allBlocks.length === 0) {
        handleEmptyState(section, pageManager, titleHeight);
        return;
    }

    if (titleHeight > pageManager.getRemainingSpace() && pageManager.currentHeightUsed > 0) {
        pageManager.startNewPage();
    }

    pageManager.recordPage(section.id);
    pageManager.addHeight(titleHeight + SECTION_GAP);
    let sectionStartPage = pageManager.currentPageIndex;

    const blocksQueue: BlockQueueItem[] = allBlocks.map((block) => ({
        block,
        measuredHeight: undefined,
    }));

    let currentSectionKey = `${section.id}-part-${pageManager.currentPageIndex}`;
    let currentSectionBlocks: TestBlock[] = [];
    let currentBlockStartIndex = 0;
    let nextBlockIndex = 0;

    const flushBlocks = () => {
        if (currentSectionBlocks.length === 0) return;

        pageManager.recordPage(section.id);
        pageManager.addToPage({
            ...section,
            key: currentSectionKey,
            blocksOverride: [...currentSectionBlocks],
            blockStartIndex: currentBlockStartIndex,
            isSplit: !isFirstChunk,
            hideTitle: pageManager.currentPageIndex > sectionStartPage,
        });
        isFirstChunk = false;
    };

    const resetForNewPage = () => {
        pageManager.startNewPage();
        currentSectionBlocks = [];
        currentSectionKey = `${section.id}-part-${pageManager.currentPageIndex}`;
        currentBlockStartIndex = nextBlockIndex;
    };

    while (blocksQueue.length > 0) {
        const item = blocksQueue.shift();
        if (!item) break;

        const { block, measuredHeight } = item;
        const uniqueKey = (block.id || block.order).toString();
        const normalizedBlockId = stripSplitBlockId(uniqueKey);
        const blockHeight =
            measuredHeight !== undefined
                ? measuredHeight
                : (measurements[getBlockMeasurementId(normalizedBlockId)] ?? DEFAULT_BLOCK_HEIGHT);
        const blockFootnotes = footnotePlan?.byBlockId[normalizedBlockId] || [];
        const footnoteHeight = getFootnoteAdditionalHeight(
            blockFootnotes,
            measurements,
            pageManager.hasFootnotesOnCurrentPage(),
        );

        const isEditingBlock =
            editingBlockId === uniqueKey ||
            (typeof editingBlockId === 'string' &&
                stripSplitBlockId(editingBlockId) === normalizedBlockId);
        const gap = currentSectionBlocks.length > 0 ? BLOCK_GAP : 0;
        const cost = blockHeight + gap + footnoteHeight;
        const remainingSpace = pageManager.getRemainingSpace();

        // The edited block must stay whole (the floating editor anchors to it),
        // but only on the page where it would naturally start — not glued onto
        // whichever page happens to be filling when the walk reaches it.
        let pinEditedBlockHere = false;
        if (isEditingBlock && !item.pinWhole && cost > remainingSpace) {
            if (pageManager.isFreshPage()) {
                pinEditedBlockHere = true;
            } else if (block.block_type === 'text') {
                const probe = splitHtmlContent(block.content || '', remainingSpace - gap, {
                    containerWidth: CONTENT_WIDTH,
                    fontConfig: data.customizations?.font,
                    allowOversizeMediaScaling: false,
                });
                // Mirror the non-editing placement: the head lands here iff some
                // content fits. Not conditioned on probe.remaining — cost/probe
                // measurement drift would otherwise push the block down a page.
                pinEditedBlockHere = !probe.pending && !!probe.fitted && probe.fittedHeight > 0;
            }
        }

        if (cost <= remainingSpace || item.pinWhole || pinEditedBlockHere) {
            currentSectionBlocks.push(block);
            pageManager.addHeight(cost);
            pageManager.addFootnotes(blockFootnotes);
            nextBlockIndex++;
            continue;
        }

        const isFreshPage = pageManager.isFreshPage();

        if (block.block_type === 'table' && !isEditingBlock) {
            if (currentSectionBlocks.length > 0) {
                flushBlocks();
                resetForNewPage();
                if (isFirstChunk) {
                    sectionStartPage = pageManager.currentPageIndex;
                    pageManager.addHeight(titleHeight + SECTION_GAP);
                }
                blocksQueue.unshift(item);
                continue;
            }

            if (!isFreshPage) {
                resetForNewPage();
                if (isFirstChunk) {
                    sectionStartPage = pageManager.currentPageIndex;
                    pageManager.addHeight(titleHeight + SECTION_GAP);
                }
                blocksQueue.unshift(item);
                continue;
            }

            currentSectionBlocks.push(block);
            pageManager.addHeight(cost);
            pageManager.addFootnotes(blockFootnotes);
            nextBlockIndex++;
            continue;
        }

        const isText = block.block_type === 'text';

        if (isText && !isEditingBlock) {
            const splitResult = splitHtmlContent(block.content || '', remainingSpace - gap, {
                containerWidth: CONTENT_WIDTH,
                fontConfig: data.customizations?.font,
                allowOversizeMediaScaling: isFreshPage,
            });

            if (splitResult.pending) {
                flushBlocks();
                resetForNewPage();
                if (isFirstChunk) {
                    sectionStartPage = pageManager.currentPageIndex;
                    pageManager.addHeight(titleHeight + SECTION_GAP);
                }
                blocksQueue.unshift(item);
                continue;
            }

            if (splitResult.fitted && splitResult.fittedHeight > 0) {
                if (!splitResult.remaining) {
                    currentSectionBlocks.push({
                        ...block,
                        content: splitResult.fitted,
                    } as TestBlock);
                    pageManager.addHeight(splitResult.fittedHeight + gap);
                    nextBlockIndex++;
                    continue;
                }

                const part1Block = {
                    ...block,
                    content: splitResult.fitted,
                    id: createSplitBlockId(normalizedBlockId, `p${pageManager.currentPageIndex}`),
                } as TestBlock;

                currentSectionBlocks.push(part1Block);
                flushBlocks();
                resetForNewPage();

                if (splitResult.remaining) {
                    const part2Block = {
                        ...block,
                        content: splitResult.remaining,
                        id: createSplitBlockId(
                            normalizedBlockId,
                            `remaining-${pageManager.currentPageIndex}`,
                        ),
                    } as TestBlock;

                    blocksQueue.unshift({
                        block: part2Block,
                        measuredHeight: splitResult.remainingHeight,
                    });
                } else {
                    nextBlockIndex++;
                }
                continue;
            }
        }

        if (isFreshPage) {
            currentSectionBlocks.push(block);
            pageManager.addHeight(cost);
            pageManager.addFootnotes(blockFootnotes);
            nextBlockIndex++;
            continue;
        }

        flushBlocks();
        resetForNewPage();
        if (isFirstChunk) {
            sectionStartPage = pageManager.currentPageIndex;
            pageManager.addHeight(titleHeight + SECTION_GAP);
        }
        // pinWhole guarantees termination for an edited block whose probe finds
        // no fit even on the fresh next page (e.g. an unsplittable oversized node).
        blocksQueue.unshift(isEditingBlock ? { ...item, pinWhole: true } : item);
    }

    flushBlocks();
    pageManager.addHeight(SECTION_GAP);
}

function handleEmptyState(section: Section, pageManager: PageManager, titleHeight: number) {
    const requiredHeight = titleHeight + SECTION_GAP + EMPTY_PLACEHOLDER_HEIGHT;

    if (requiredHeight > pageManager.getRemainingSpace() && pageManager.currentHeightUsed > 0) {
        pageManager.startNewPage();
    }

    pageManager.recordPage(section.id);
    pageManager.addToPage({
        ...section,
        key: section.id,
        blocksOverride: [],
        blockStartIndex: 0,
        isSplit: false,
        hideTitle: false,
    } as RenderableSection);

    pageManager.addHeight(requiredHeight);
}
